import * as React from 'react'
import { useEffect, useState } from 'react'

import { canvasProvider, cleverProvider, googleProvider, classlinkProvider, providerConfigLookup } from './providerHelpers'
import ArchiveClassModal from './archive_classroom_modal'
import ChangeGradeModal from './change_grade_modal'
import Classroom from './classroom'
import CoteacherInvitation from './coteacher_invitation'
import CreateAClassModal from './create_a_class_modal'
import ImportProviderClassroomStudentsModal from './import_provider_classroom_students_modal'
import ImportProviderClassroomsModal from './import_provider_classrooms_modal'
import InviteStudentsModal from './invite_students_modal'
import LinkProviderAccountModal from './link_provider_account_modal'
import MergeClassesModal from './merge_classes_modal'
import MergeClassesPromptModal from './merge_classes_prompt_modal'
import NoClassroomsToImportModal from './no_classrooms_to_import_modal'
import ReauthorizeProviderModal from './reauthorize_provider_modal'
import RenameClassModal from './rename_classroom_modal'
import CanvasModal from './canvas_modal'
import ClasslinkModal from './classlink_modal'

import pusherInitializer from '../../../../modules/pusherInitializer'
import useSnackbarMonitor from '../../../Shared/hooks/useSnackbarMonitor'
import { requestGet, requestPut } from '../../../../modules/request/index'
import { SMALL_ICON_BASE_SRC, Snackbar, SortableList, defaultSnackbarTimeout, getPageTitle } from '../../../Shared/index'
import { MY_CLASSES_FEATURED_BLOG_POST_ID } from '../../constants/featuredBlogPost'
import ArticleSpotlight from '../shared/articleSpotlight'
import BulkArchiveClassesBanner from '../shared/bulk_archive_classes_banner'
import { ButtonSpinner, DragHandle } from '../../../Shared/index'
import DemoBlockedModal from '../shared/demoBlockedModal'
import DemoGuardedDropdownInput from '../shared/demoGuardedDropdownInput'
import ViewAsStudentModal from '../shared/view_as_student_modal'
import { useDemoAccountContextProvider } from '../../contexts/DemoAccountContextProvider'

export const createAClassModal = 'createAClassModal'
export const classlinkUpsellModal = 'classlinkUpsellModal'
export const renameClassModal = 'renameClassModal'
export const changeGradeModal = 'changeGradeModal'
export const archiveClassModal = 'archiveClassModal'
export const mergeClassesModal = 'mergeClassesModal'
export const inviteStudentsModal = 'inviteStudentsModal'
export const importProviderClassroomsModal = 'importProviderClassroomsModal'
export const importProviderClassroomStudentsModal = 'importProviderClassroomStudentsModal'
export const linkCanvasAccountModal = 'linkCanvasAccountModal'
export const linkCleverAccountModal = 'linkCleverAccountModal'
export const linkGoogleAccountModal = 'linkGoogleAccountModal'
export const reauthorizeProviderModal = 'reauthorizeProviderModal'
export const noClassroomsToImportModal = 'noClassroomsToImportModal'
export const viewAsStudentModal = 'viewAsStudentModal'

const canvasIconSrc = `${window.postbuildEnv.CDN_URL}/images/icons/canvas.svg`
const cleverIconSrc = `${window.postbuildEnv.CDN_URL}/images/icons/clever.svg`
const googleClassroomIconSrc = `${window.postbuildEnv.CDN_URL}/images/icons/google-classroom.svg`
const classlinkClassroomIconSrc = `${window.postbuildEnv.CDN_URL}/images/shared/classlink_icon_sm.png`

const reorderSrc = `${SMALL_ICON_BASE_SRC}/reorder.svg`

const MERGE_PROMPT_MINIMUM_CLASS_COUNT = 8
const MERGE_PROMPT_MAXIMUM_AVERAGE_STUDENT_COUNT = 3

export const getOwnedImportedClassrooms = (classrooms: Array<any>, userId: number) => {
  return classrooms.filter(c => {
    const owner = c.teachers?.find(t => t.classroom_relation === 'owner')
    return c.classroomProvider && owner?.id === userId
  })
}

export const getAlreadyImportedProvider = (classrooms: Array<any>, userId: number, currentProviderTitle: string) => {
  if (!currentProviderTitle) { return null }

  const conflictingClassroom = getOwnedImportedClassrooms(classrooms, userId).find(c => c.classroomProvider && c.classroomProvider !== currentProviderTitle)

  return conflictingClassroom?.classroomProvider || null
}

export const meetsMergePromptCriteria = (classrooms: Array<any>, userId: number) => {
  const importedClassrooms = getOwnedImportedClassrooms(classrooms, userId)

  if (importedClassrooms.length < MERGE_PROMPT_MINIMUM_CLASS_COUNT) { return false }

  const totalStudents = importedClassrooms.reduce((sum, c) => sum + c.students.length, 0)
  return Math.floor(totalStudents / importedClassrooms.length) < MERGE_PROMPT_MAXIMUM_AVERAGE_STUDENT_COUNT
}

interface ActiveClassroomsProps {
  canvasLink: string
  classrooms: Array<any>
  cleverLink: string
  googleLink: string
  coteacherInvitations: Array<any>
  showMergeClassesPrompt?: boolean
  user: any
}

const ActiveClassrooms = ({
  canvasLink,
  classrooms: initialClassrooms,
  cleverLink,
  coteacherInvitations: initialCoteacherInvitations,
  googleLink,
  showMergeClassesPrompt,
  user,
}: ActiveClassroomsProps) => {
  document.title = getPageTitle("Manage Classes", "Active Classes")
  const { provider } = user
  const providerConfig = providerConfigLookup[provider]

  const reauthorizeLink = {
    [canvasProvider]: canvasLink,
    [cleverProvider]: cleverLink,
    [googleProvider]: googleLink,
    [classlinkProvider]: ''
  }[provider]

  const [classrooms, setClassrooms] = useState(initialClassrooms.filter(classroom => classroom.visible))
  const [coteacherInvitations, setCoteacherInvitations] = useState(initialCoteacherInvitations)
  const [allProviderClassrooms, setAllProviderClassrooms] = useState([])
  const [providerClassrooms, setProviderClassrooms] = useState([])
  const [providerClassroomsLoading, setProviderClassroomsLoading] = useState(false)
  const [pendingImportFromProviderRequest, setPendingImportFromProviderRequest] = useState(false)

  const [isSnackbarVisible, setIsSnackbarVisible] = useState(false)
  const [snackbarCopy, setSnackbarCopy] = useState('')
  const [visibleModal, setVisibleModal] = useState(null)
  const [selectedClassroomId, setSelectedClassroomId] = useState(null)
  const [mergeImportClassroom, setMergeImportClassroom] = useState(null)
  const [showDemoBlockedModal, setShowDemoBlockedModal] = useState(false)
  const [showMergePrompt, setShowMergePrompt] = useState(!!showMergeClassesPrompt && meetsMergePromptCriteria(initialClassrooms.filter(classroom => classroom.visible), user.id))

  const { isDemo } = useDemoAccountContextProvider()

  const providerIconSrc = {
    [canvasProvider]: canvasIconSrc,
    [cleverProvider]: cleverIconSrc,
    [googleProvider]: googleClassroomIconSrc,
    [classlinkProvider]: classlinkClassroomIconSrc
  }

  useSnackbarMonitor(isSnackbarVisible, setIsSnackbarVisible, defaultSnackbarTimeout)

  useEffect(() => {
    setStateBasedOnParams()
  }, [])

  useEffect(() => {
    if (!pendingImportFromProviderRequest) { return }
    if (providerClassrooms === null) { return }

    setPendingImportFromProviderRequest(false)
    openModal(providerClassrooms.length ? importProviderClassroomsModal : noClassroomsToImportModal)
  }, [providerClassrooms])

  function handleImportClassesClick(option) {
    return importFromProvider[option.value]()
  }

  const closeModal = (callback = null) => {
    setVisibleModal(null)
    setMergeImportClassroom(null)

    if (callback && typeof (callback) === 'function') { callback() }
  }

  const getClassroomCardsWithHandle = (classroomCards) => {
    // using a div instead of a button because button behavior overrides the keypress handling by sortable
    return classroomCards.map(card => (
      <div className="sortable-classroom-item" key={card.key}>
        <span className='reorder-classroom-item'>
          <DragHandle>
            <div className="interactive-wrapper focus-on-light">
              <img alt="" className="reorder-icon" src={reorderSrc} />
            </div>
          </DragHandle>
        </span>
        {card}
      </div>
    ));
  }

  const clickClassroomHeader = (classroomId) => {
    setSelectedClassroomId(selectedClassroomId === classroomId ? null : classroomId)
  }

  const getClassroomsAndCoteacherInvitations = () => {
    requestGet('/teachers/classrooms', (body) => {
      setClassrooms(body.classrooms.filter(classroom => classroom.visible))
      setCoteacherInvitations(body.coteacher_invitations)
    })
  }

  const getOwnActiveClassrooms = (classrooms) => {
    return classrooms.filter(c => {
      const classroomOwner = c.teachers.find(teacher => teacher.classroom_relation === 'owner')
      return c.visible && classroomOwner?.id === user.id
    })
  }

  const importFromCanvas = () => {
    if (providerConfig?.isCanvas) {
      setPendingImportFromProviderRequest(true)
      retrieveProviderClassrooms()
    } else {
      openModal(linkCanvasAccountModal)
    }
  }

  const importFromClever = () => {
    if (providerConfig?.isClever) {
      setPendingImportFromProviderRequest(true)
      retrieveProviderClassrooms()
    } else {
      openModal(linkCleverAccountModal)
    }
  }

  const importFromGoogle = () => {
    if (providerConfig?.isGoogle) {
      setPendingImportFromProviderRequest(true)
      retrieveProviderClassrooms()
    } else {
      openModal(linkGoogleAccountModal)
    }
  }

  const importFromClasslink = () => openModal(classlinkUpsellModal)

  const importFromProvider = {
    [canvasProvider]: importFromCanvas,
    [cleverProvider]: importFromClever,
    [googleProvider]: importFromGoogle,
    [classlinkProvider]: importFromClasslink,
  }

  const importProviderClassroomStudents = () => {
    if (!providerConfig?.retrieveClassroomsPath) { return }

    requestGet(providerConfig.retrieveClassroomsPath, (body) => {
      if (body.reauthorization_required) {
        openModal(reauthorizeProviderModal)
      } else {
        openModal(importProviderClassroomStudentsModal)
      }
    })
  }

  const importMergedSecondaryStudents = (secondaryClassroom) => {
    if (!providerConfig?.retrieveClassroomsPath) { return }

    requestGet(providerConfig.retrieveClassroomsPath, (body) => {
      if (body.reauthorization_required) {
        openModal(reauthorizeProviderModal)
      } else {
        setMergeImportClassroom(secondaryClassroom)
        openModal(importProviderClassroomStudentsModal)
      }
    })
  }

  const onSuccess = (snackbarCopy: string) => {
    getClassroomsAndCoteacherInvitations()
    retrieveProviderClassrooms()
    showSnackbar(snackbarCopy)
    closeModal()
  }

  const openModal = (modalName: string) => {
    setVisibleModal(modalName)
  }

  const retrieveProviderClassrooms = () => {
    if (!providerConfig?.retrieveClassroomsPath) { return }

    pusherInitializer(user.id, providerConfig.retrieveClassroomsEventName, retrieveProviderClassrooms)
    setProviderClassroomsLoading(true)

    requestGet(providerConfig.retrieveClassroomsPath, (body) => {
      if (body.reauthorization_required) {
        openModal(reauthorizeProviderModal)
        return
      }

      if (body.quill_retrieval_processing) { return }

      setAllProviderClassrooms(body.classrooms)

      if (providerConfig?.isGoogle) {
        setProviderClassrooms(body.classrooms.filter(classroom => !classroom.alreadyImported && classroom.is_owner))
      } else {
        setProviderClassrooms(body.classrooms.filter(classroom => !classroom.alreadyImported))
      }

      setProviderClassroomsLoading(false)
    })
  }

  const setStateBasedOnParams = () => {
    const urlParams = new URLSearchParams(window.location.search)
    const modal = urlParams.get('modal')
    const classroomId = urlParams.get('classroom')

    setSelectedClassroomId(Number(classroomId) || null)

    switch (modal) {
      case 'create-a-class':
        setVisibleModal(createAClassModal)
        break
      case 'google-classroom':
        importFromGoogle()
        break
      case 'invite-students':
        setVisibleModal(inviteStudentsModal)
        break
      case 'view-as-student':
        setVisibleModal(viewAsStudentModal)
        break
    }

    if (visibleModal || selectedClassroomId) {
      setVisibleModal(visibleModal)
      setSelectedClassroomId(selectedClassroomId)
    }
  }

  const showSnackbar = snackbarCopy => {
    setSnackbarCopy(snackbarCopy)
    setIsSnackbarVisible(true)
  }

  const sortClassrooms = (sortedClassroomObjects) => {
    const newlySortedClassrooms = sortedClassroomObjects.map((classroomObject, i) => {
      const { props } = classroomObject
      const { children } = props
      let classroom = children[1].props.classroom
      classroom.order = i
      return classroom
    })

    requestPut('/classrooms_teachers/update_order', { updated_classrooms: JSON.stringify(newlySortedClassrooms) }, body => {
      const { classrooms } = body
      if (classrooms) { setClassrooms(newlySortedClassrooms) }
    })
  }

  const viewAsStudent = (id = null) => {
    if (id) {
      window.location.href = `/teachers/preview_as_student/${id}`
    } else {
      openModal(viewAsStudentModal)
    }
  }

  const renderBulkArchiveClassesBanner = () => {
    const ownedClassrooms = classrooms.filter(c => {
      const classroomOwner = c.teachers.find(t => t.classroom_relation === 'owner')
      return classroomOwner && classroomOwner.id === user.id
    })

    return (
      <BulkArchiveClassesBanner
        classes={ownedClassrooms}
        onSuccess={onSuccess}
        userId={user.id}
      />
    )
  }

  const getAvailableClassroomsForMerge = (primaryClassroomId) => {
    return getOwnActiveClassrooms(classrooms).filter(c => c.id !== primaryClassroomId && !c.classroom_merges?.length)
  }

  const renderClassroomCards = (ownActiveClassrooms) => {
    return classrooms.map(classroom => {
      const isOwnedByCurrentUser = !!ownActiveClassrooms.find(c => c.id === classroom.id)

      return (
        <Classroom
          archiveClass={() => openModal(archiveClassModal)}
          changeGrade={() => openModal(changeGradeModal)}
          classroom={classroom}
          classrooms={ownActiveClassrooms}
          clickClassroomHeader={clickClassroomHeader}
          importMergedSecondaryStudents={importMergedSecondaryStudents}
          importProviderClassroomStudents={importProviderClassroomStudents}
          inviteStudents={() => openModal(inviteStudentsModal)}
          isOwnedByCurrentUser={isOwnedByCurrentUser}
          key={classroom.id}
          mergeClasses={getAvailableClassroomsForMerge(classroom.id).length ? () => openModal(mergeClassesModal) : null}
          onSuccess={onSuccess}
          renameClass={() => openModal(renameClassModal)}
          selected={classroom.id === selectedClassroomId}
          user={user}
          viewAsStudent={viewAsStudent}
        />
      )
    })
  }

  const renderClassroomRows = (ownActiveClassrooms) => {
    const classroomCards = renderClassroomCards(ownActiveClassrooms)
    const rows = getClassroomCardsWithHandle(classroomCards)

    return (
      <SortableList
        data={rows}
        disabled={isDemo}
        helperClass="dragged-sortable-classroom"
        onDisabledInteraction={() => setShowDemoBlockedModal(true)}
        sortCallback={sortClassrooms}
        useDragHandle={true}
      />
    )
  }

  const handleCreateAClassClick = () => openModal(createAClassModal)

  const renderCreateAClassButton = () => {
    return (
      <button
        className="quill-button small primary contained create-a-class-button focus-on-light"
        onClick={handleCreateAClassClick}
        type="button"
      >
        Create a class
      </button>
    )
  }

  const renderCreateAClassModal = () => {
    if (visibleModal === createAClassModal) {
      return (
        <CreateAClassModal
          close={() => closeModal(getClassroomsAndCoteacherInvitations)}
          showSnackbar={showSnackbar}
        />
      )
    }
  }

  const renderInviteStudentsModal = () => {
    const selectedClassroom = classrooms.find(c => c.id === selectedClassroomId)

    if (visibleModal === inviteStudentsModal && selectedClassroom) {
      return (
        <InviteStudentsModal
          classroom={selectedClassroom}
          close={() => closeModal(getClassroomsAndCoteacherInvitations)}
          showSnackbar={showSnackbar}
        />
      )
    }
  }

  const renderChangeGradeModal = () => {
    const selectedClassroom = classrooms.find(c => c.id === selectedClassroomId)

    if (visibleModal === changeGradeModal && selectedClassroom) {
      return (
        <ChangeGradeModal
          classroom={selectedClassroom}
          close={closeModal}
          onSuccess={onSuccess}
        />
      )
    }
  }

  const renderArchiveClassModal = () => {
    const selectedClassroom = classrooms.find(c => c.id === selectedClassroomId)

    if (visibleModal === archiveClassModal && selectedClassroom) {
      return (
        <ArchiveClassModal
          classroom={selectedClassroom}
          close={closeModal}
          onSuccess={onSuccess}
        />
      )
    }
  }

  const renderMergeClassesPromptModal = () => {
    if (!showMergePrompt || visibleModal) { return null }

    const importedClassrooms = getOwnedImportedClassrooms(classrooms, user.id)

    if (!importedClassrooms.length) { return null }

    const totalStudents = importedClassrooms.reduce((sum, c) => sum + c.students.length, 0)

    return (
      <MergeClassesPromptModal
        averageStudentsPerClass={Math.floor(totalStudents / importedClassrooms.length)}
        close={() => setShowMergePrompt(false)}
        numberOfImportedClasses={importedClassrooms.length}
      />
    )
  }

  const renderMergeClassesModal = () => {
    const selectedClassroom = classrooms.find(c => c.id === selectedClassroomId)

    if (visibleModal === mergeClassesModal && selectedClassroom) {
      const availableClassrooms = getAvailableClassroomsForMerge(selectedClassroom.id)

      return (
        <MergeClassesModal
          availableClassrooms={availableClassrooms}
          classroom={selectedClassroom}
          close={closeModal}
          onSuccess={onSuccess}
        />
      )
    }
  }

  const renderHeader = () => {
    return (
      <div className="header">
        <h1>Active Classes</h1>
        <div className="buttons">
          { renderImportClassesControl() }
          {renderCreateAClassButton()}
        </div>
      </div>
    )
  }

  // A provider whose classes are synced for the teacher offers no import control at all.
  const renderImportClassesControl = () => {
    if (!provider) { return renderImportClassesDropdown() }
    if (providerConfig?.importsClassrooms === false) { return null }

    return renderImportFromProviderButton(provider)
  }

  const renderImportFromProviderButton = (theProvider: string) => {
    const theProviderTitle = providerConfigLookup[theProvider].title

    let buttonContent = <React.Fragment>Import from {theProviderTitle}</React.Fragment>
    let buttonClassName = "quill-button outlined small grey import-from-provider-button focus-on-light"

    if (providerClassroomsLoading && pendingImportFromProviderRequest) {
      buttonContent = <React.Fragment>Import from {theProviderTitle}<ButtonSpinner variant="dark" /></React.Fragment>
      buttonClassName += ' loading'
    }

    const importFromTheProvider = importFromProvider[theProvider]
    const alt = `${theProviderTitle} Icon`
    const src = providerIconSrc[theProvider]

    return (
      <button aria-label={`Import from ${theProviderTitle}`} className={buttonClassName} onClick={importFromTheProvider} type="button">
        <img alt={alt} className='import-from-provider-button-icon' src={src} />
        {buttonContent}
      </button>
    )
  }

  const renderImportClassesDropdown = () => {
    const options = [
      {
        label: 'Import from Clever',
        value: cleverProvider,
      },
      {
        label: 'Import from Google Classroom',
        value: googleProvider,
      },
      {
        label: 'Import from ClassLink',
        value: classlinkProvider,
      },
      {
        label: 'Import from Canvas',
        value: canvasProvider,
      }
    ]

    return (
      <DemoGuardedDropdownInput
        ariaLabel="Import classes"
        className="import-classes-dropdown-input small bordered icon focus-on-light"
        handleChange={handleImportClassesClick}
        options={options}
        value={{ label: 'Import Classes', value: null }}
      />
    )
  }

  const renderImportProviderClassroomStudentsModal = () => {
    const selectedClassroom = mergeImportClassroom || classrooms.find(c => c.id === selectedClassroomId)

    if (visibleModal === importProviderClassroomStudentsModal && selectedClassroom) {
      return (
        <ImportProviderClassroomStudentsModal
          classroom={selectedClassroom}
          close={closeModal}
          onSuccess={onSuccess}
          provider={provider}
          user={user}
        />
      )
    }
  }

  const renderImportProviderClassroomsModal = () => {
    if (visibleModal !== importProviderClassroomsModal) { return null }

    return (
      <ImportProviderClassroomsModal
        alreadyImportedProvider={getAlreadyImportedProvider(classrooms, user.id, providerConfig?.title)}
        classrooms={providerClassrooms}
        close={() => closeModal(() => setPendingImportFromProviderRequest(false))}
        onSuccess={onSuccess}
        provider={provider}
        user={user}
      />
    )
  }

  const renderLinkProviderAccountModal = () => {
    let link = ''
    let linkAccountProvider = ''

    if (visibleModal === linkCleverAccountModal) {
      link = cleverLink
      linkAccountProvider = cleverProvider
    } else if (visibleModal === linkGoogleAccountModal) {
      // no link assignment since google uses a different component for linking accounts
      linkAccountProvider = googleProvider
    } else if (visibleModal === linkCanvasAccountModal) {
      return <CanvasModal close={closeModal} user={user} />
    } else if (visibleModal === classlinkUpsellModal) {
      return ( <ClasslinkModal close={closeModal} user={user} />)
    } else {
      return null
    }

    return (
      <LinkProviderAccountModal
        close={closeModal}
        link={link}
        provider={linkAccountProvider}
        user={user}
      />
    )
  }

  const renderPageContent = () => {
    const ownActiveClassrooms = getOwnActiveClassrooms(classrooms)

    if (classrooms.length === 0 && coteacherInvitations.length === 0) {

      return (
        <div className="no-active-classes">
          <h2>Add your first class</h2>
          <p>All teachers need a class! Choose to create or import your classes. </p>
        </div>
      )
    } else {
      const coteacherInvitationCards = coteacherInvitations.map(coteacherInvitation => {
        return (
          <CoteacherInvitation
            coteacherInvitation={coteacherInvitation}
            getClassroomsAndCoteacherInvitations={getClassroomsAndCoteacherInvitations}
            key={coteacherInvitation.id}
            showSnackbar={showSnackbar}
          />
        )
      })
      const classroomCards = renderClassroomRows(ownActiveClassrooms)
      return (
        <div className="active-classes">
          {coteacherInvitationCards}
          {classroomCards}
        </div>
      )
    }
  }

  const renderNoClassroomsToImportModal = () => {
    if (visibleModal === noClassroomsToImportModal) {
      return <NoClassroomsToImportModal allProviderClassrooms={allProviderClassrooms} close={closeModal} provider={provider} />
    }
  }

  const renderReauthorizeProviderModal = () => {
    return visibleModal === reauthorizeProviderModal
      ? <ReauthorizeProviderModal close={closeModal} link={reauthorizeLink} provider={provider} />
      : null
  }

  const renderRenameClassModal = () => {
    const selectedClassroom = classrooms.find(c => c.id === selectedClassroomId)

    if (visibleModal === renameClassModal && selectedClassroom) {
      return (
        <RenameClassModal
          classroom={selectedClassroom}
          close={closeModal}
          onSuccess={onSuccess}
        />
      )
    }
  }

  const renderSnackbar = () => {
    return <Snackbar text={snackbarCopy} visible={isSnackbarVisible} />
  }

  const renderViewAsStudentModal = () => {
    if (visibleModal === viewAsStudentModal) {
      return (
        <ViewAsStudentModal
          classrooms={classrooms}
          close={closeModal}
          defaultClassroomId={selectedClassroomId}
          handleViewClick={viewAsStudent}
        />
      )
    }
  }

  return (
    <React.Fragment>
      {showDemoBlockedModal && <DemoBlockedModal onClose={() => setShowDemoBlockedModal(false)} />}
      <main className="container gray-background-accommodate-footer active-classrooms classrooms-page" id="post-secondary-nav">
        {renderCreateAClassModal()}
        {renderRenameClassModal()}
        {renderChangeGradeModal()}
        {renderArchiveClassModal()}
        {renderMergeClassesModal()}
        {renderMergeClassesPromptModal()}
        {renderInviteStudentsModal()}
        {renderImportProviderClassroomsModal()}
        {renderImportProviderClassroomStudentsModal()}
        {renderReauthorizeProviderModal()}
        {renderLinkProviderAccountModal()}
        {renderNoClassroomsToImportModal()}
        {renderViewAsStudentModal()}
        {renderSnackbar()}
        {renderBulkArchiveClassesBanner()}
        {renderHeader()}
        {renderPageContent()}
      </main>
      <ArticleSpotlight blogPostId={MY_CLASSES_FEATURED_BLOG_POST_ID} />
    </React.Fragment>
  )
}

export default ActiveClassrooms
